import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw, Play } from 'lucide-react';
import {
  getWorkflow, listExecutionsPage, startManualExecution,
} from 'features/agents/api/agentsApi';
import { isRunActive } from 'features/agents/lib/status';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import usePolling from 'shared/hooks/usePolling';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import IssuesFilter from 'features/issues/components/IssuesFilter';
import TableFooter from 'shared/components/tables/TableFooter';
import { activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import RunsTable from 'features/agents/runs/RunsTable';
import RunStats from 'features/agents/runs/RunStats';
import RunDrawer from 'features/agents/runs/RunDrawer';

const PAGE = 25;
const STATUS_OPTIONS = [
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];
const matchesStatus = (run, statuses) => {
  const s = String(run.status || '');
  return statuses.some((x) => (x === 'running' ? (s === 'running' || s === 'queued') : s === x));
};

/** Newest first, one row per run, whichever page it came from. */
function mergeRuns(fresh, old) {
  const seen = new Set();
  return [...fresh, ...old]
    .filter((r) => (seen.has(r.id) ? false : seen.add(r.id)))
    .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
}

/**
 * Runs of ONE agent, reached from that agent: Agents / <agent> / Runs. A
 * status filter, the last 7 days at a glance, a table, and a drawer for a
 * single run. Another agent's runs are reached from that agent, not here.
 * The route stays `/:team/agents/:id/executions`.
 */
export default function ExecutionsPage({ workflowId: idProp }) {
  const params = useParams();
  const workflowId = idProp || params.workflowId;
  const team = params.team;
  const navigate = useNavigate();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [workflow, setWorkflow] = useState(null);
  const base = `/${encodeURIComponent(team)}/agents`;
  useBreadcrumb(useMemo(() => [
    { label: 'Agents', to: base },
    { label: workflow?.name || '…', to: `${base}/${workflowId}` },
    { label: 'Runs' },
  ], [base, workflow, workflowId]));

  const [statuses, setStatuses] = useState([]);
  // The API takes one status: one chosen is asked for, two are filtered here.
  const status = statuses.length === 1 ? statuses[0] : '';
  const clientFilter = statuses.length > 1 && statuses.length < STATUS_OPTIONS.length;
  const [rows, setRows] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [starting, setStarting] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let live = true;
    getWorkflow(workflowId).then((w) => { if (live) setWorkflow(w); }).catch(() => {});
    return () => { live = false; };
  }, [workflowId]);

  const first = useCallback(async () => {
    const page = await listExecutionsPage(workflowId, { limit: PAGE, status: status || undefined });
    setStats(page.stats);
    return page;
  }, [workflowId, status]);

  // A new agent or filter starts the list over.
  useEffect(() => {
    let live = true;
    setRows(null); setCursor(null); setError('');
    first().then((p) => { if (live) { setRows(p.executions); setCursor(p.next_cursor); } })
      .catch((e) => { if (live) { setError(e?.response?.data?.error || e.message); setRows([]); } });
    return () => { live = false; };
  }, [first]);

  const reload = useCallback(async () => {
    try {
      const p = await first();
      setRows((cur) => mergeRuns(p.executions, cur || []));
      setError('');
      return p.executions;
    } catch (e) { setError(e?.response?.data?.error || e.message); return undefined; }
  }, [first]);

  const anyActive = (rows || []).some((r) => isRunActive(r.status));
  usePolling(async () => {
    const fresh = await reload();
    return fresh ? fresh.some((r) => isRunActive(r.status)) : true;
  }, { enabled: anyActive, interval: 3000, maxInterval: 15000, immediate: false, key: `${workflowId}:${status}` });

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const p = await listExecutionsPage(workflowId, { limit: PAGE, cursor, status: status || undefined });
      setRows((cur) => mergeRuns(cur || [], p.executions));
      setCursor(p.next_cursor);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoadingMore(false); }
  };

  const runNow = async () => {
    setStarting(true);
    try {
      const exec = await startManualExecution(workflowId, {});
      await reload();
      setOpenId(exec.id);
    } catch (e) { setError(e?.response?.data?.error || e?.message); }
    finally { setStarting(false); }
  };

  const onChanged = async (next) => {
    await reload();
    if (next?.id) setOpenId(next.id);
  };
  const visible = rows && clientFilter ? rows.filter((r) => matchesStatus(r, statuses)) : rows;
  const openRow = rows?.find((r) => r.id === openId);
  const statusSection = { id: 'status', label: 'Status', options: STATUS_OPTIONS, selected: statuses, onChange: setStatuses, emptyIsAny: true };

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex shrink-0 flex-wrap items-center gap-2 bg-surface px-3 pb-2 pt-3 sm:px-4">
        <IssuesFilter sections={[statusSection]} />
        <div className="flex-1" />
        <button type="button" onClick={reload} className={activityToolbarButtonClass}>
          <RefreshCw size={13} aria-hidden /> Refresh
        </button>
        {canWrite && workflow?.published_version_id && (
          <button type="button" onClick={runNow} disabled={starting} className={activityToolbarButtonClass}>
            <Play size={12} aria-hidden /> {starting ? 'Starting…' : 'Run now'}
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-3 px-4 pb-8 pt-1">
          <RunStats stats={stats} />
          {error && <div className="rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger" role="alert">{error}</div>}
          {rows === null && <PageLoader label="Loading runs" />}
          {visible !== null && visible.length === 0 && !error && (
            <div className="flex h-48 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
              {statuses.length ? 'No runs match this filter.' : 'No runs yet.'}
            </div>
          )}
          {visible !== null && visible.length > 0 && (
            <>
              <RunsTable rows={visible} selectedId={openId} onOpen={(r) => setOpenId(r.id)} />
              <TableFooter shown={visible.length} hasMore={Boolean(cursor)} loading={loadingMore} onLoadMore={loadMore} />
            </>
          )}
        </div>
      </div>

      {openId && (
        <RunDrawer
          executionId={openId}
          row={openRow}
          workflowName={workflow?.name}
          canWrite={canWrite}
          onClose={() => setOpenId(null)}
          onChanged={onChanged}
          onOpenAgent={() => navigate(`${base}/${workflowId}`)}
        />
      )}
    </main>
  );
}
